import { useState, useEffect, useMemo, useRef } from 'react';
import { format, parseISO, isAfter } from 'date-fns';
import { useDisplaySettings, useEventsRange } from '../../api/hooks';
import { TimeTilePopover } from './TimeTilePopover';
import { trackEvent } from '../../lib/analytics';
import type { CalendarEvent } from '../../types/calendar';

/**
 * TimeTile - Displays current time in landscape horizontal layout
 *
 * Placed in sidebar below header, updates every 60 seconds.
 *
 * Features:
 * - Horizontal time display (hours:minutes with colon)
 * - AM/PM indicator (12h mode only)
 * - Click to show popover with full date and event details
 */
export function TimeTile() {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showPopover, setShowPopover] = useState(false);
  const tileRef = useRef<HTMLDivElement>(null);

  const { data: displaySettings } = useDisplaySettings();
  const { data: eventsData } = useEventsRange(7); // Next 7 days of events (for popover)

  const timeFormat = displaySettings?.timeFormat || '12h';

  // Update time every 60 seconds, synchronized to minute boundaries.
  //
  // Also refresh immediately whenever the page becomes visible or regains focus.
  // On a kiosk tablet the WebView is paused while the screen sleeps, which
  // freezes this interval — so without this, the displayed time stays stuck at
  // the pre-sleep minute until the next tick after wake (the "delayed clock"
  // people see when they walk up to the display). new Date() is always correct;
  // this just forces the rendered value to catch up the moment we're visible.
  useEffect(() => {
    const update = () => setCurrentTime(new Date());

    // Calculate milliseconds until the next minute
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    let intervalId: NodeJS.Timeout | null = null;

    // Set timeout to sync to the next minute boundary
    const syncTimeout = setTimeout(() => {
      update();

      // Now set up the regular 60-second interval
      intervalId = setInterval(update, 60000);
    }, msUntilNextMinute);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') update();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', update);

    return () => {
      clearTimeout(syncTimeout);
      if (intervalId) {
        clearInterval(intervalId);
      }
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', update);
    };
  }, []);

  // Track component mount
  useEffect(() => {
    trackEvent('clock_tile_viewed', {
      time_format: timeFormat,
      timestamp: currentTime.toISOString(),
    });
  }, []); // Only on mount

  // Get next 3 upcoming events (for popover only)
  const upcomingEvents = useMemo(() => {
    if (!eventsData?.eventsByDate) return [];

    const now = new Date();
    const allEvents: CalendarEvent[] = [];

    // Collect all events from the eventsByDate structure
    Object.values(eventsData.eventsByDate).forEach((memberEvents) => {
      Object.values(memberEvents).forEach((dayEvents) => {
        allEvents.push(...dayEvents);
      });
    });

    // Filter to future events only (not started yet)
    const futureEvents = allEvents.filter((event) => {
      const eventStart = parseISO(event.startTime);
      return isAfter(eventStart, now);
    });

    // Sort by start time
    futureEvents.sort((a, b) => {
      const aStart = parseISO(a.startTime);
      const bStart = parseISO(b.startTime);
      return aStart.getTime() - bStart.getTime();
    });

    // Return top 3
    return futureEvents.slice(0, 3);
  }, [eventsData]);

  // Format time components
  const hours = format(currentTime, timeFormat === '12h' ? 'h' : 'HH');
  const minutes = format(currentTime, 'mm');
  const period = timeFormat === '12h' ? format(currentTime, 'a') : null;

  const handleClick = () => {
    setShowPopover(true);
    trackEvent('clock_tile_clicked', {
      time_format: timeFormat,
      upcoming_events_count: upcomingEvents.length,
    });
  };

  const handleClosePopover = () => {
    setShowPopover(false);
  };

  return (
    <>
      <div
        ref={tileRef}
        onClick={handleClick}
        className="w-full h-16 md:h-[76px] lg:h-20 rounded-xl bg-gradient-to-br from-bg-primary to-bg-surface shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer flex items-center justify-center border border-border-light"
        data-testid="time-tile"
      >
        {/* Time display - horizontal landscape layout */}
        <div className="flex items-baseline gap-0.5">
          {/* Hours */}
          <div
            className="text-4xl lg:text-5xl font-bold leading-none text-text-primary tabular-nums"
            data-testid="time-tile-hours"
          >
            {hours}
          </div>

          {/* Colon */}
          <div className="text-4xl lg:text-5xl font-bold leading-none text-text-primary">:</div>

          {/* Minutes */}
          <div
            className="text-4xl lg:text-5xl font-bold leading-none text-text-primary tabular-nums"
            data-testid="time-tile-minutes"
          >
            {minutes}
          </div>

          {/* Period (12h only) */}
          {period && (
            <div
              className="text-sm lg:text-base font-medium text-text-muted uppercase ml-1.5 self-end mb-1"
              data-testid="time-tile-period"
            >
              {period}
            </div>
          )}
        </div>
      </div>

      {/* Popover */}
      {showPopover && (
        <TimeTilePopover
          currentTime={currentTime}
          upcomingEvents={upcomingEvents}
          onClose={handleClosePopover}
          anchorRef={tileRef}
        />
      )}
    </>
  );
}
